import { z } from 'zod';

import { IsoDateTimeSchema } from './common.js';

/**
 * Products (catalog) dashboard - completeness / "ready to quote" health for the
 * six product masters. Grounded in the itinerary pricing path: the one rule
 * that blocks a quote everywhere is a missing live priced rate (silent 0). For
 * hotels and cruises that priced rate must also be date-valid, and those two
 * are the only types the builder gates on ACTIVE status. Approval and image are
 * quality-only - the activity / transfer / visa / insurance pickers ignore
 * them, so they are reported but never make a product "not ready".
 */

export const PRODUCT_TYPES = [
  'activities',
  'transfers',
  'hotels',
  'cruises',
  'visas',
  'insurance',
] as const;
export const ProductTypeSchema = z.enum(PRODUCT_TYPES);
export type ProductType = z.infer<typeof ProductTypeSchema>;

export const ProductCompletenessRowSchema = z.object({
  /** Catalog ids can be uuid v4 or v7, so not pinned to v7. */
  id: z.string(),
  title: z.string(),
  /** DRAFT | PENDING_APPROVAL | ACTIVE | INACTIVE | ARCHIVED. */
  status: z.string(),
  /** City / country label for display; null when none is set. */
  geo: z.string().nullable(),
  /**
   * Structured geography for the by-city / by-country breakdown (null when
   * unset). Defaulted so the admin still renders if it is briefly ahead of a
   * not-yet-redeployed API that does not return these fields.
   */
  cityId: z.string().nullable().default(null),
  cityName: z.string().nullable().default(null),
  countryId: z.string().nullable().default(null),
  countryName: z.string().nullable().default(null),
  /** Has a city / country (or, for insurance, is scoped) so it can surface in a picker. */
  hasGeo: z.boolean(),
  /** Has a live priced rate (date-valid for hotels / cruises). The one hard quote blocker. */
  priced: z.boolean(),
  /** Fully approved (admin + moderator where moderator applies). Governance only. */
  approved: z.boolean(),
  /** Has at least one image. Display only. */
  hasImage: z.boolean(),
  /**
   * Long-haul product, derived from its country: the Europe region plus Japan,
   * South Korea and Sri Lanka (the not-yet-fully-managed catalog; the set lives
   * in the API). That catalog is not fully managed yet, so a long-haul product
   * that is not ready is reported as "skipped" rather than counted as blocked /
   * not-ready. Every other country (incl. India, Vietnam, UAE, ...) is
   * short-haul and still counts as blocked when unready.
   *
   * Defaulted (short-haul) for resilience if the API is briefly behind the admin.
   */
  isLongHaul: z.boolean().default(false),
});
export type ProductCompletenessRow = z.infer<typeof ProductCompletenessRowSchema>;

export const ProductsBreakdownResponseSchema = z.object({
  activities: z.array(ProductCompletenessRowSchema),
  transfers: z.array(ProductCompletenessRowSchema),
  hotels: z.array(ProductCompletenessRowSchema),
  cruises: z.array(ProductCompletenessRowSchema),
  visas: z.array(ProductCompletenessRowSchema),
  insurance: z.array(ProductCompletenessRowSchema),
  generatedAt: IsoDateTimeSchema,
});
export type ProductsBreakdownResponse = z.infer<typeof ProductsBreakdownResponseSchema>;

/** The types whose itinerary pickers also require ACTIVE status + date-valid rates. */
export const PRODUCT_TYPES_WITH_STATUS_GATE: ReadonlyArray<ProductType> = ['hotels', 'cruises'];
